<script setup>
import { h, computed, ref, watch } from 'vue';
import { provideSidebarContext } from './provider';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useMapGetter } from 'dashboard/composables/store';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useStorage } from '@vueuse/core';
import { useSidebarKeyboardShortcuts } from './useSidebarKeyboardShortcuts';
import { vOnClickOutside } from '@vueuse/components';
import { useLeadsterUrls } from 'dashboard/composables/useLeadsterUrls';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useOnboardingResumeMenuItem } from './useOnboardingResumeMenuItem';
import { useRouteAccess } from 'dashboard/composables/useRouteTabs';

import SidebarGroup from './SidebarGroup.vue';
import SidebarOnboardingCard from './SidebarOnboardingCard.vue';
import SidebarAccountSwitcher from './SidebarAccountSwitcher.vue';
import SidebarCollapsedNav from './SidebarCollapsedNav.vue';
import SidebarRail from './SidebarRail.vue';
import SidebarProductHeader from './SidebarProductHeader.vue';
import SidebarProfileMenu from './SidebarProfileMenu.vue';
import SidebarSearchItem from './SidebarSearchItem.vue';
import ChannelLeaf from './ChannelLeaf.vue';
import { SIDEBAR_DIVIDER } from './sidebarMenuItemClasses';
import {
  PRODUCT_WEBSITE,
  PRODUCT_WHATSAPP,
  SCRIPTS_KIND_STORAGE_KEY,
  SIDEBAR_ASSETS,
  SIDEBAR_WIDTH,
  SIDEBAR_COLLAPSED_WIDTH,
  HELP_CENTER_URL,
  accentColor,
} from './unifiedSidebar';
import { useRouter } from 'vue-router';

const props = defineProps({
  isMobileSidebarOpen: {
    type: Boolean,
    default: false,
  },
  isLeftSidebarOpen: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits([
  'closeKeyShortcutModal',
  'openKeyShortcutModal',
  'showCreateAccountModal',
  'closeMobileSidebar',
  'toggleLeftSidebar',
]);

const { accountScopedRoute, accountId, currentAccount } = useAccount();
const store = useStore();
const { t } = useI18n();
const router = useRouter();
const menuHovered = ref(false);
const selectedProductKey = ref(PRODUCT_WHATSAPP);
const notificationMetadata = useMapGetter('notifications/getMeta');

const unreadNotificationCount = computed(() => {
  const count = notificationMetadata.value?.unreadCount;
  if (!count) return '';
  return count < 100 ? `${count}` : '99+';
});

const goToNotifications = () => {
  router.push({ name: 'notifications_index' });
};

const toggleShortcutModalFn = show => {
  if (show) {
    emit('openKeyShortcutModal');
  } else {
    emit('closeKeyShortcutModal');
  }
};

useSidebarKeyboardShortcuts(toggleShortcutModalFn);

const expandedItem = useStorage(
  'next-sidebar-expanded-item',
  null,
  sessionStorage
);

const setExpandedItem = name => {
  expandedItem.value = expandedItem.value === name ? null : name;
};
provideSidebarContext({
  expandedItem,
  setExpandedItem,
});

const inboxes = useMapGetter('inboxes/getInboxes');
const labels = useMapGetter('labels/getLabelsOnSidebar');
const teams = useMapGetter('teams/getMyTeams');
const contactCustomViews = useMapGetter('customViews/getContactCustomViews');
const conversationCustomViews = useMapGetter(
  'customViews/getConversationCustomViews'
);

watch(
  accountId,
  id => {
    if (!id) return;
    store.dispatch('labels/get');
    store.dispatch('inboxes/fetchFromNetwork');
    store.dispatch('notifications/unReadCount');
    store.dispatch('teams/get');
    store.dispatch('attributes/get');
    store.dispatch('customViews/get', 'conversation');
    store.dispatch('customViews/get', 'contact');
  },
  { immediate: true }
);

const sortedInboxes = computed(() =>
  (inboxes.value || []).slice().sort((a, b) => a.name.localeCompare(b.name))
);

const isGupshupIntegrationEnabled = computed(() =>
  store.getters['accounts/isFeatureEnabledonAccount'](
    accountId.value,
    FEATURE_FLAGS.GUPSHUP_INTEGRATION
  )
);

const closeMobileSidebar = () => {
  if (!props.isMobileSidebarOpen) return;
  emit('closeMobileSidebar');
};

const newReportRoutes = () => [
  {
    name: 'Reports Agent',
    label: t('SIDEBAR.REPORTS_AGENT'),
    to: accountScopedRoute('agent_reports_index'),
    activeOn: ['agent_reports_show'],
  },
  {
    name: 'Reports Label',
    label: t('SIDEBAR.REPORTS_LABEL'),
    to: accountScopedRoute('label_reports_index'),
  },
  {
    name: 'Reports Inbox',
    label: t('SIDEBAR.REPORTS_INBOX'),
    to: accountScopedRoute('inbox_reports_index'),
    activeOn: ['inbox_reports_show'],
  },
  {
    name: 'Reports Team',
    label: t('SIDEBAR.REPORTS_TEAM'),
    to: accountScopedRoute('team_reports_index'),
    activeOn: ['team_reports_show'],
  },
];

const reportRoutes = computed(() => newReportRoutes());
const {
  scriptsUrl,
  leadsterAiUrl,
  leadsDashboardUrl,
  marketingIntegrationsUrl,
} = useLeadsterUrls();

const openLeadsterSameTab = url => {
  window.location.href = url;
};

const { isAdmin } = useAdmin();
const hasWhatsappQuota = computed(() => {
  const account = currentAccount.value || {};
  if (typeof account.whatsapp_quota_configured === 'boolean') {
    return account.whatsapp_quota_configured;
  }
  const quota = account.whatsapp_quota || {};
  if (quota.configured === false) return false;
  return quota.configured === true || quota.plan_contacts != null;
});
const { menuItem: onboardingResumeMenuItem } = useOnboardingResumeMenuItem();
const { isRouteAllowed } = useRouteAccess();

// Tabbed pages (Campaigns, Automations) are single menu entries; link to the
// first tab the user can access and hide the entry when there is none.
const firstAllowedRoute = routeNames =>
  routeNames.map(name => accountScopedRoute(name)).find(isRouteAllowed);

const CAMPAIGN_ROUTES = [
  'campaigns_whatsapp_index',
  'campaigns_multi_step_index',
];
const CAMPAIGN_PAGE_ROUTES = [
  ...CAMPAIGN_ROUTES,
  'campaigns_whatsapp_analytics',
  'whatsapp_flow_edit',
  'whatsapp_flow_tree',
];
const AUTOMATION_ROUTES = [
  'automations_follow_ups',
  'automations_reminders',
  'automations_rules',
  'automations_macros',
  'automations_canned',
];
const AUTOMATION_PAGE_ROUTES = [
  ...AUTOMATION_ROUTES,
  'follow_up_flow_new',
  'follow_up_flow_edit',
  'follow_up_flow_analytics',
  'macros_new',
  'macros_edit',
];

const products = computed(() => {
  const whatsappProduct = {
    key: PRODUCT_WHATSAPP,
    label: t('SIDEBAR.PRODUCT_WHATSAPP'),
    accent: 'blue',
    iconUrl: SIDEBAR_ASSETS.whatsappIcon,
    logoUrl: SIDEBAR_ASSETS.whatsappLogo,
    logoDarkUrl: SIDEBAR_ASSETS.whatsappLogoDark,
  };

  if (!isAdmin.value) {
    return [whatsappProduct];
  }

  return [
    {
      key: PRODUCT_WEBSITE,
      label: t('SIDEBAR.PRODUCT_WEBSITE'),
      accent: 'blue',
      iconUrl: SIDEBAR_ASSETS.websiteIcon,
      logoUrl: SIDEBAR_ASSETS.websiteLogo,
      logoDarkUrl: SIDEBAR_ASSETS.websiteLogoDark,
    },
    whatsappProduct,
  ];
});

const activeProduct = computed(() => {
  return (
    products.value.find(product => product.key === selectedProductKey.value) ||
    products.value.find(product => product.key === PRODUCT_WHATSAPP) ||
    products.value[0]
  );
});

const collapsed = computed(() => !props.isLeftSidebarOpen);

watch(
  isAdmin,
  admin => {
    if (!admin && selectedProductKey.value === PRODUCT_WEBSITE) {
      selectedProductKey.value = PRODUCT_WHATSAPP;
      localStorage.setItem(SCRIPTS_KIND_STORAGE_KEY, 'whatsapp');
    }
  },
  { immediate: true }
);

watch(
  collapsed,
  value => {
    document.documentElement.style.setProperty(
      '--leadster-sidebar-offset',
      value ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH
    );
    localStorage.setItem('leadster.sidebarCollapsed', value ? '1' : '0');
  },
  { immediate: true }
);

watch(
  activeProduct,
  product => {
    if (!product) return;
    const color = accentColor(product.accent);
    document.documentElement.style.setProperty('--sidebar-accent', color);
    document.documentElement.style.setProperty(
      '--sidebar-active-bg',
      `color-mix(in srgb, ${color} 12%, transparent)`
    );
    document.documentElement.style.setProperty(
      '--sidebar-hover-bg',
      `color-mix(in srgb, ${color} 6%, transparent)`
    );
  },
  { immediate: true }
);

const SWITCH_SKELETON_MS = 500;
const switchingProduct = ref(false);
let switchTimer;

const selectProduct = product => {
  if (!isAdmin.value && product.key === PRODUCT_WEBSITE) return;

  if (product.key === PRODUCT_WEBSITE) {
    localStorage.setItem(SCRIPTS_KIND_STORAGE_KEY, 'website');
    openLeadsterSameTab(leadsDashboardUrl.value);
    return;
  }

  if (product.key !== selectedProductKey.value) {
    switchingProduct.value = true;
    clearTimeout(switchTimer);
    switchTimer = setTimeout(() => {
      switchingProduct.value = false;
    }, SWITCH_SKELETON_MS);
  }

  selectedProductKey.value = product.key;
  localStorage.setItem(SCRIPTS_KIND_STORAGE_KEY, 'whatsapp');
};

const leadsterMenuItems = computed(() => {
  if (!isAdmin.value) return [];

  return [
    {
      name: 'Scripts',
      label: t('SIDEBAR.LEADSTER_AI'),
      icon: 'i-lucide-zap',
      to: null,
      onClick: () => openLeadsterSameTab(leadsterAiUrl.value),
    },
    {
      name: 'ManageScripts',
      label: t('SIDEBAR.SCRIPTS'),
      icon: 'i-lucide-message-circle',
      to: null,
      onClick: () => openLeadsterSameTab(scriptsUrl.value),
    },
    {
      name: 'LeadsterIntegrations',
      label: t('SIDEBAR.LEADSTER_INTEGRATIONS'),
      icon: 'i-lucide-share-2',
      to: null,
      onClick: () => openLeadsterSameTab(marketingIntegrationsUrl.value),
    },
  ];
});

const contentTemplateChildren = computed(() => {
  const twilioChildren = sortedInboxes.value
    .filter(
      inbox =>
        inbox.channel_type === 'Channel::TwilioSms' &&
        inbox.medium === 'whatsapp'
    )
    .map(inbox => ({
      name: `${inbox.name}-${inbox.id}`,
      label: inbox.name,
      to: accountScopedRoute('content_templates_inbox', {
        inbox_id: inbox.id,
      }),
    }));

  const whatsappChildren = sortedInboxes.value
    .filter(
      inbox =>
        inbox.channel_type === 'Channel::Whatsapp' ||
        (inbox.channel_type === 'Channel::Gupshup' &&
          isGupshupIntegrationEnabled.value)
    )
    .map(inbox => ({
      name: `whatsapp-${inbox.id}`,
      label: inbox.name,
      to: accountScopedRoute('whatsapp_templates_inbox', {
        inbox_id: inbox.id,
      }),
    }));

  return [...twilioChildren, ...whatsappChildren];
});

const atendimentoMenuItems = computed(() => {
  return [
    {
      name: 'Inbox',
      label: t('SIDEBAR.INBOX'),
      icon: 'i-lucide-inbox',
      to: accountScopedRoute('inbox_view'),
      activeOn: ['inbox_view', 'inbox_view_conversation'],
      getterKeys: {
        count: 'notifications/getUnreadCount',
      },
    },
    {
      name: 'Conversation',
      label: t('SIDEBAR.CONVERSATIONS'),
      icon: 'i-lucide-message-circle',
      children: [
        {
          name: 'All',
          label: t('SIDEBAR.ALL_CONVERSATIONS'),
          activeOn: ['inbox_conversation'],
          to: accountScopedRoute('home'),
        },
        {
          name: 'Mentions',
          label: t('SIDEBAR.MENTIONED_CONVERSATIONS'),
          activeOn: ['conversation_through_mentions'],
          to: accountScopedRoute('conversation_mentions'),
        },
        {
          name: 'Unattended',
          activeOn: ['conversation_through_unattended'],
          label: t('SIDEBAR.UNATTENDED_CONVERSATIONS'),
          to: accountScopedRoute('conversation_unattended'),
        },
        {
          name: 'Folders',
          label: t('SIDEBAR.CUSTOM_VIEWS_FOLDER'),
          icon: 'i-lucide-folder',
          activeOn: ['conversations_through_folders'],
          children: conversationCustomViews.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            to: accountScopedRoute('folder_conversations', { id: view.id }),
          })),
        },
        {
          name: 'Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-lucide-users',
          activeOn: ['conversations_through_team'],
          children: teams.value.map(team => ({
            name: `${team.name}-${team.id}`,
            label: team.name,
            to: accountScopedRoute('team_conversations', { teamId: team.id }),
          })),
        },
        {
          name: 'Channels',
          label: t('SIDEBAR.CHANNELS'),
          icon: 'i-lucide-mailbox',
          activeOn: ['conversation_through_inbox'],
          children: sortedInboxes.value.map(inbox => ({
            name: `${inbox.name}-${inbox.id}`,
            label: inbox.name,
            to: accountScopedRoute('inbox_dashboard', { inbox_id: inbox.id }),
            component: leafProps =>
              h(ChannelLeaf, {
                label: leafProps.label,
                active: leafProps.active,
                inbox,
              }),
          })),
        },
        {
          name: 'Labels',
          label: t('SIDEBAR.LABELS'),
          icon: 'i-lucide-tag',
          activeOn: ['conversations_through_label'],
          children: labels.value.map(label => ({
            name: `${label.title}-${label.id}`,
            label: label.title,
            icon: h('span', {
              class: `size-[12px] ring-1 ring-n-alpha-1 dark:ring-white/20 ring-inset rounded-sm`,
              style: { backgroundColor: label.color },
            }),
            to: accountScopedRoute('label_conversations', {
              label: label.title,
            }),
          })),
        },
      ],
    },
    {
      name: 'Contacts',
      label: t('SIDEBAR.CONTACTS'),
      icon: 'i-lucide-contact',
      children: [
        {
          name: 'All Contacts',
          label: t('SIDEBAR.ALL_CONTACTS'),
          to: accountScopedRoute(
            'contacts_dashboard_index',
            {},
            { page: 1, search: undefined }
          ),
          activeOn: ['contacts_dashboard_index', 'contacts_edit'],
        },
        {
          name: 'Active',
          label: t('SIDEBAR.ACTIVE'),
          to: accountScopedRoute('contacts_dashboard_active'),
          activeOn: ['contacts_dashboard_active'],
        },
        {
          name: 'Segments',
          icon: 'i-lucide-group',
          label: t('SIDEBAR.CUSTOM_VIEWS_SEGMENTS'),
          children: contactCustomViews.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            to: accountScopedRoute(
              'contacts_dashboard_segments_index',
              { segmentId: view.id },
              { page: 1 }
            ),
            activeOn: [
              'contacts_dashboard_segments_index',
              'contacts_edit_segment',
            ],
          })),
        },
        {
          name: 'Tagged With',
          icon: 'i-lucide-tag',
          label: t('SIDEBAR.TAGGED_WITH'),
          children: labels.value.map(label => ({
            name: `${label.title}-${label.id}`,
            label: label.title,
            icon: h('span', {
              class: `size-[12px] ring-1 ring-n-alpha-1 dark:ring-white/20 ring-inset rounded-sm`,
              style: { backgroundColor: label.color },
            }),
            to: accountScopedRoute(
              'contacts_dashboard_labels_index',
              { label: label.title },
              { page: 1, search: undefined }
            ),
            activeOn: [
              'contacts_dashboard_labels_index',
              'contacts_edit_label',
            ],
          })),
        },
      ],
    },
    contentTemplateChildren.value.length > 0 && {
      name: 'ContentTemplates',
      label: t('SIDEBAR.CONTENT_TEMPLATES'),
      icon: 'i-lucide-notebook-text',
      activeOn: [
        'content_templates_index',
        'content_templates_inbox',
        'whatsapp_templates_inbox',
      ],
      children: contentTemplateChildren.value,
    },
    firstAllowedRoute(CAMPAIGN_ROUTES) && {
      name: 'Campaigns',
      label: t('SIDEBAR.CAMPAIGNS'),
      icon: 'i-lucide-megaphone',
      to: firstAllowedRoute(CAMPAIGN_ROUTES),
      activeOn: CAMPAIGN_PAGE_ROUTES,
    },
    firstAllowedRoute(AUTOMATION_ROUTES) && {
      name: 'Automations',
      label: t('SIDEBAR.AUTOMATIONS'),
      icon: 'i-lucide-rocket',
      to: firstAllowedRoute(AUTOMATION_ROUTES),
      activeOn: AUTOMATION_PAGE_ROUTES,
    },
    {
      name: 'Reports',
      label: t('SIDEBAR.REPORTS'),
      icon: 'i-lucide-chart-column',
      children: [
        ...(isAdmin.value && hasWhatsappQuota.value
          ? [
              {
                name: 'Report Plan Usage',
                label: t('SIDEBAR.REPORTS_PLAN_USAGE'),
                to: accountScopedRoute('plan_usage_reports'),
              },
            ]
          : []),
        {
          name: 'Report Overview',
          label: t('SIDEBAR.REPORTS_OVERVIEW'),
          to: accountScopedRoute('account_overview_reports'),
        },
        {
          name: 'Report Conversation',
          label: t('SIDEBAR.REPORTS_CONVERSATION'),
          to: accountScopedRoute('conversation_reports'),
        },
        ...reportRoutes.value,
        {
          name: 'Reports CSAT',
          label: t('SIDEBAR.CSAT'),
          to: accountScopedRoute('csat_reports'),
        },
        {
          name: 'Reports SLA',
          label: t('SIDEBAR.REPORTS_SLA'),
          to: accountScopedRoute('sla_reports'),
        },
        {
          name: 'Reports Bot',
          label: t('SIDEBAR.REPORTS_BOT'),
          to: accountScopedRoute('bot_reports'),
        },
      ],
    },
    {
      name: 'Settings',
      label: t('SIDEBAR.SETTINGS'),
      icon: 'i-lucide-settings',
      children: [
        {
          name: 'Settings Account Settings',
          label: t('SIDEBAR.ACCOUNT_SETTINGS'),
          to: accountScopedRoute('general_settings_index'),
        },
        {
          name: 'Settings Agents',
          label: t('SIDEBAR.AGENTS'),
          to: accountScopedRoute('agent_list'),
        },
        {
          name: 'Settings Teams',
          label: t('SIDEBAR.TEAMS'),
          to: accountScopedRoute('settings_teams_list'),
        },
        {
          name: 'Settings Inboxes',
          label: t('SIDEBAR.INBOXES'),
          to: accountScopedRoute('settings_inbox_list'),
        },
        {
          name: 'Settings Labels',
          label: t('SIDEBAR.LABELS'),
          to: accountScopedRoute('labels_list'),
        },
        {
          name: 'Settings Custom Attributes',
          label: t('SIDEBAR.CUSTOM_ATTRIBUTES'),
          to: accountScopedRoute('attributes_list'),
        },
        {
          name: 'Settings Agent Bots',
          label: t('SIDEBAR.AGENT_BOTS'),
          to: accountScopedRoute('agent_bots'),
        },
        {
          name: 'Settings Integrations',
          label: t('SIDEBAR.INTEGRATIONS'),
          to: accountScopedRoute('settings_applications'),
        },
        {
          name: 'Settings Audit Logs',
          label: t('SIDEBAR.AUDIT_LOGS'),
          to: accountScopedRoute('auditlogs_list'),
        },
        {
          name: 'Settings Custom Roles',
          label: t('SIDEBAR.CUSTOM_ROLES'),
          to: accountScopedRoute('custom_roles_list'),
        },
        {
          name: 'Settings Sla',
          label: t('SIDEBAR.SLA'),
          to: accountScopedRoute('sla_list'),
        },
        {
          name: 'Settings Billing',
          label: t('SIDEBAR.BILLING'),
          to: accountScopedRoute('billing_settings_index'),
        },
      ],
    },
  ].filter(Boolean);
});

const footerMenuItems = computed(() => [
  {
    name: 'Portals',
    label: t('SIDEBAR.HELP_CENTER.TITLE'),
    icon: 'i-lucide-help-circle',
    to: null,
    rightIcon: 'i-lucide-external-link',
    onClick: () =>
      window.open(HELP_CENTER_URL, '_blank', 'noopener,noreferrer'),
  },
]);

const whatsappPanelMenuItems = computed(() => [
  ...leadsterMenuItems.value,
  ...(leadsterMenuItems.value.length
    ? [{ name: 'LeadsterDivider', kind: 'divider' }]
    : []),
  ...atendimentoMenuItems.value,
]);

const panelMenuItems = computed(() => whatsappPanelMenuItems.value);

const collapsedSearchItem = computed(() => ({
  name: 'Search',
  label: t('COMBOBOX.SEARCH_PLACEHOLDER'),
  icon: 'i-lucide-search',
  to: { name: 'search' },
  activeOn: ['search'],
}));

const collapsedMenuItems = computed(() => [
  collapsedSearchItem.value,
  ...leadsterMenuItems.value,
  ...atendimentoMenuItems.value,
  ...footerMenuItems.value,
]);

const shellStyle = computed(() => {
  const color = accentColor(activeProduct.value?.accent || 'blue');
  return {
    '--sidebar-accent': color,
    '--sidebar-active-bg': `color-mix(in srgb, ${color} 12%, transparent)`,
    '--sidebar-hover-bg': `color-mix(in srgb, ${color} 6%, transparent)`,
    width: collapsed.value ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH,
  };
});
</script>

<template>
  <aside
    v-on-click-outside="[
      closeMobileSidebar,
      { ignore: ['#mobile-sidebar-launcher'] },
    ]"
    class="unified-sidebar bg-white dark:bg-n-solid-2 flex flex-row items-stretch text-[15px] font-normal leading-[1.3] text-n-slate-12 fixed top-0 ltr:left-0 rtl:right-0 h-full z-40 transition-[width] duration-200 ease-in-out font-sans ltr:border-r rtl:border-l border-n-weak overflow-visible"
    :class="[
      {
        'shadow-lg': isMobileSidebarOpen,
        'ltr:-translate-x-full rtl:translate-x-full md:ltr:translate-x-0 md:rtl:translate-x-0':
          !isMobileSidebarOpen,
      },
    ]"
    :style="shellStyle"
    @mouseenter="menuHovered = true"
    @mouseleave="menuHovered = false"
  >
    <SidebarRail
      :products="products"
      :active-product-key="activeProduct.key"
      :collapsed="collapsed"
      :menu-hovered="menuHovered"
      @select-product="selectProduct"
      @toggle-collapse="emit('toggleLeftSidebar')"
      @open-key-shortcut-modal="emit('openKeyShortcutModal')"
    />

    <div
      class="flex flex-col flex-1 min-w-0 min-h-0 bg-white dark:bg-n-solid-2 pt-[0.85rem] pb-3 transition-[width] duration-200 ease-in-out overflow-visible"
      :class="collapsed ? 'md:max-w-[4.5rem]' : 'md:max-w-[15rem]'"
    >
      <div class="flex flex-col w-full min-h-0 flex-1 overflow-visible">
        <SidebarProductHeader
          :product="activeProduct"
          :products="products"
          :active-product-key="activeProduct.key"
          :collapsed="collapsed"
          @select-product="selectProduct"
        />

        <div
          v-if="!collapsed"
          class="relative z-10 flex-shrink-0 px-3 pt-3 overflow-visible"
        >
          <SidebarOnboardingCard
            v-if="onboardingResumeMenuItem"
            class="mb-3"
            v-bind="onboardingResumeMenuItem"
          />
          <SidebarAccountSwitcher
            class="min-w-0"
            @show-create-account-modal="emit('showCreateAccountModal')"
          />
        </div>

        <nav
          class="flex-grow min-h-0 overflow-y-auto overflow-x-hidden no-scrollbar pt-[0.35rem]"
        >
          <ul
            v-if="switchingProduct"
            class="flex flex-col gap-3 m-0 list-none p-3 animate-pulse"
            aria-hidden="true"
          >
            <li v-for="n in 6" :key="n" class="flex items-center gap-3">
              <span class="size-5 rounded bg-n-alpha-2 flex-shrink-0" />
              <span v-if="!collapsed" class="h-4 flex-1 rounded bg-n-alpha-2" />
            </li>
          </ul>

          <ul
            v-else-if="!collapsed"
            class="flex flex-col m-0 list-none p-0"
          >
            <SidebarSearchItem />

            <template v-for="item in panelMenuItems" :key="item.name">
              <li
                v-if="item.kind === 'divider'"
                :class="SIDEBAR_DIVIDER"
                aria-hidden="true"
              />
              <SidebarGroup
                v-else
                v-bind="item"
              />
            </template>

            <li :class="SIDEBAR_DIVIDER" aria-hidden="true" />

            <SidebarGroup
              v-for="item in footerMenuItems"
              :key="item.name"
              v-bind="item"
            />
          </ul>

          <div v-else class="w-full">
            <SidebarCollapsedNav :items="collapsedMenuItems" />
          </div>
        </nav>

        <!-- Mobile-only: rail is hidden below md, so profile/notifications live here -->
        <div
          class="md:hidden flex-shrink-0 flex items-center gap-2 px-3 pt-2 mt-auto border-t border-n-weak"
        >
          <button
            type="button"
            class="relative flex items-center justify-center size-10 rounded-lg border-0 bg-transparent text-n-slate-12 flex-shrink-0"
            :title="t('SIDEBAR.NOTIFICATIONS')"
            :aria-label="t('SIDEBAR.NOTIFICATIONS')"
            @click="goToNotifications"
          >
            <span class="i-lucide-bell size-5" />
            <span
              v-if="unreadNotificationCount"
              class="min-h-2 min-w-2 p-0.5 px-1 bg-n-ruby-9 rounded-md absolute top-1 right-1 grid place-items-center text-[9px] leading-none text-n-ruby-3"
            >
              {{ unreadNotificationCount }}
            </span>
          </button>
          <div class="min-w-0 flex-1">
            <SidebarProfileMenu
              @open-key-shortcut-modal="emit('openKeyShortcutModal')"
            />
          </div>
        </div>
      </div>
    </div>
  </aside>

  <!-- Spacer so flex layout keeps content clear of the fixed sidebar on desktop -->
  <div
    class="hidden md:block flex-shrink-0 transition-[width] duration-200 ease-in-out"
    :style="{ width: collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH }"
    aria-hidden="true"
  />
</template>
